import { z } from 'zod';
import { CONTENT_LIMITS, HASHTAG_PATTERN } from '../content/content.js';
import { AI_COPY_LIMITS, copyClaimSchema } from './copy.js';
/**
 * `reviser` (spec 80 §4.7, S80-6, T80-6-4): applies one reviewer comment to the
 * texts of a content item and commits exactly one new version on
 * `base_version_id` (`author_type = ai_job`, `change_kind = text_changed`), or
 * answers `cannot_apply` with a reason. It never changes media, status,
 * approvals or comments (spec 80 §9 Q9 interim: a comment asking for other
 * media is `cannot_apply`).
 */
export const REVISER_OUTCOMES = ['changes', 'cannot_apply'];
export const REVISER_LIMITS = {
    /** A comment as module 50 stores it (`COMMENT_MAX_LENGTH`). */
    maxCommentLength: 5000,
    /** At most one change per localization; an item has few languages. */
    maxChanges: 10,
    maxSummaryLength: 500,
    maxReasonLength: 500,
};
/**
 * Params module 50 passes through `AiJobsService.enqueue` (spec 50 §5): the
 * comment and the item's current version as the base.
 */
export const reviserInputSchema = z
    .object({
    content_item_id: z.uuid(),
    base_version_id: z.uuid(),
    comment_id: z.uuid(),
    comment_body: z.string().min(1).max(REVISER_LIMITS.maxCommentLength),
})
    .strict();
/**
 * The new texts of one localization; `null` keeps a field unchanged. `claims`
 * are the factual statements of the changed text with the verified knowledge
 * items they rest on (REQ-80-014). Per-platform copy is not modelled (spec 80
 * §9 Q3), so a change is keyed by the localization's language only.
 */
export const reviserChangeSchema = z
    .object({
    /** The localization's `language` as given in the context (BCP-47, or `mul`). */
    language: z.string().min(1).max(35),
    headline: z.string().max(CONTENT_LIMITS.headlineMaxLength).nullable(),
    caption: z.string().min(1).max(CONTENT_LIMITS.captionMaxLength).nullable(),
    cta: z.string().max(CONTENT_LIMITS.ctaMaxLength).nullable(),
    hashtags: z.array(z.string().regex(HASHTAG_PATTERN)).max(CONTENT_LIMITS.maxHashtags).nullable(),
    claims: z.array(copyClaimSchema).max(AI_COPY_LIMITS.maxClaims),
})
    .strict();
/**
 * Either `changes` (+ `summary`) or `cannot_apply` (+ `reason`); `comment_id`
 * repeats the comment so the stored run output references it (AC-80-038).
 * The pairing rules are checked by the job's validation, not by zod, so the
 * provider schema stays strict.
 */
export const reviserOutputSchema = z
    .object({
    comment_id: z.string(),
    outcome: z.enum(REVISER_OUTCOMES),
    changes: z.array(reviserChangeSchema).max(REVISER_LIMITS.maxChanges),
    summary: z.string().min(1).max(REVISER_LIMITS.maxSummaryLength).nullable(),
    reason: z.string().min(1).max(REVISER_LIMITS.maxReasonLength).nullable(),
})
    .strict();
//# sourceMappingURL=reviser.js.map